<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { WizardPage } from '@/types/wizard-forms'

const props = defineProps<{
  pages: WizardPage[]
  activePageId: number | null
  fixedPageActive: boolean
}>()

const emit = defineEmits<{
  select: [pageId: number]
  reorder: [pageIds: number[]]
  add: []
  delete: [pageId: number]
  'select-fixed': []
}>()

const { t } = useI18n()

const dragSourceId = ref<number | null>(null)
const dragOverId = ref<number | null>(null)

function onDragStart(pageId: number): void {
  dragSourceId.value = pageId
}

function onDragOver(event: DragEvent, pageId: number): void {
  event.preventDefault()
  dragOverId.value = pageId
}

function onDrop(targetPageId: number): void {
  if (dragSourceId.value === null || dragSourceId.value === targetPageId) {
    dragSourceId.value = null
    dragOverId.value = null
    return
  }

  const sorted = [...props.pages].sort((a, b) => a.sort_order - b.sort_order)
  const sourceIndex = sorted.findIndex((p) => p.id === dragSourceId.value)
  const targetIndex = sorted.findIndex((p) => p.id === targetPageId)

  if (sourceIndex === -1 || targetIndex === -1) {
    dragSourceId.value = null
    dragOverId.value = null
    return
  }

  const reordered = [...sorted]
  const [moved] = reordered.splice(sourceIndex, 1)
  reordered.splice(targetIndex, 0, moved)

  emit('reorder', reordered.map((p) => p.id))
  dragSourceId.value = null
  dragOverId.value = null
}

function onDragEnd(): void {
  dragSourceId.value = null
  dragOverId.value = null
}
</script>

<template>
  <div class="flex flex-col gap-1">
    <!-- Fixed Page 1 — always first, read-only -->
    <div
      class="flex items-center gap-2 rounded-lg px-3 py-2.5 cursor-pointer transition-colors select-none"
      :class="[
        props.fixedPageActive
          ? 'bg-amber-500/15 border border-amber-500/40 text-amber-300'
          : 'bg-slate-800/60 border border-slate-700 text-slate-300 hover:bg-slate-700/60 hover:text-white',
      ]"
      @click="emit('select-fixed')"
    >
      <svg
        class="w-3.5 h-3.5 text-amber-500/70 shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
        />
      </svg>
      <span class="text-sm font-medium truncate flex-1">{{ t('wizard_forms.fixed_page.title') }}</span>
      <span class="shrink-0 inline-flex items-center rounded-full bg-amber-500/15 border border-amber-500/30 px-1.5 py-0.5 text-xs font-medium text-amber-400">
        {{ t('wizard_forms.fixed_page.badge') }}
      </span>
    </div>

    <div
      v-for="page in [...props.pages].sort((a, b) => a.sort_order - b.sort_order)"
      :key="page.id"
      draggable="true"
      class="group flex items-center gap-2 rounded-lg px-3 py-2.5 cursor-pointer transition-colors select-none"
      :class="[
        props.activePageId === page.id
          ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-300'
          : 'bg-slate-800/60 border border-slate-700 text-slate-300 hover:bg-slate-700/60 hover:text-white',
        dragOverId === page.id && dragSourceId !== page.id ? 'border-emerald-400 scale-[1.01]' : '',
      ]"
      @click="emit('select', page.id)"
      @dragstart="onDragStart(page.id)"
      @dragover="onDragOver($event, page.id)"
      @drop="onDrop(page.id)"
      @dragend="onDragEnd"
    >
      <svg
        class="w-3.5 h-3.5 text-slate-500 group-hover:text-slate-400 cursor-grab shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
      </svg>
      <span class="text-sm font-medium truncate flex-1">{{ page.title }}</span>
      <button
        type="button"
        class="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-300 p-0.5 rounded"
        :title="t('common.delete')"
        @click.stop="emit('delete', page.id)"
      >
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <button
      type="button"
      class="mt-2 flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-emerald-400 hover:text-emerald-300 border border-dashed border-emerald-500/40 hover:border-emerald-500/70 transition-colors"
      @click="emit('add')"
    >
      <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
      </svg>
      {{ t('wizard_forms.pages.add_page') }}
    </button>
  </div>
</template>
